import { OrbitControls } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { useMemo } from 'react'
import { HATCH_COMPACTO } from '../../dominio/didatico/perfilVeiculo'
import type { PrioridadeTriagem } from '../../dominio/didatico/ocupantes'
import type { ObjetoTabletop } from '../../dominio/tabletop'
import { PalcoEstudio, CONFIGURACAO_GL_ESTUDIO } from '../ambiente/PalcoEstudio'
import { ProvedorQualidade } from '../ambiente/contextoQualidade'
import { ContadorFps } from '../ambiente/ContadorFps'
import { MonitorQualidade } from '../ambiente/MonitorQualidade'
import { PosProcessamento } from '../ambiente/PosProcessamento'
import { useQualidadeAdaptativa } from '../ambiente/useQualidadeAdaptativa'
import { RECURSOS_QUALIDADE } from '../ambiente/qualidadeGrafica'
import { CenaTabletop } from './CenaTabletop'
import type { AparenciaRisco } from '../../dominio/tabletop/atendimento/riscos'

const NOOP = () => {}

interface Props {
  /** Objetos já filtrados pelo estado público (`montarEstadoPublico`). */
  readonly objetos: readonly ObjetoTabletop[]
  readonly triagensDeclaradas: Readonly<Record<string, PrioridadeTriagem>>
  readonly colaresColocados: Readonly<Record<string, boolean>>
  readonly sangramentos: Readonly<Record<string, 'ativo' | 'parcial'>>
  readonly riscos: Readonly<Record<string, AparenciaRisco>>
  readonly exibirAsfalto?: boolean
}

/**
 * Cena 3D da 2ª tela / projetor: a mesma `CenaTabletop` do instrutor, travada
 * (`somenteLeitura`, sem seleção nem gizmo) e sempre no modo aluno — a cor
 * da vítima vem da triagem que a equipe declarou, nunca da referência. A
 * turma pode girar e aproximar a câmera, mas nada do que faz aqui volta para
 * a janela do instrutor.
 */
export function VisualizadorProjecaoTabletop({ objetos, triagensDeclaradas, colaresColocados, sangramentos, riscos, exibirAsfalto = true }: Props) {
  const qualidade = useQualidadeAdaptativa()
  const atendimento = useMemo(

    () => ({ modoExibicao: 'aluno' as const, triagensDeclaradas, colaresColocados, sangramentos, riscos }),
    [triagensDeclaradas, colaresColocados, sangramentos, riscos],
  )

  return (
    <div className="visualizador-3d">
      <Canvas
        camera={{ position: [7.5, 6.2, 8.5], fov: 42 }}
        dpr={RECURSOS_QUALIDADE[qualidade.nivel].dpr}
        shadows="percentage"
        gl={CONFIGURACAO_GL_ESTUDIO}
      >
        <MonitorQualidade
          adaptativa={qualidade.adaptativa}
          aoDecair={qualidade.aoDecair}
          aoSubir={qualidade.aoSubir}
        />
        <ContadorFps qualidade={qualidade.nivel} />
        <PalcoEstudio
          perfil={HATCH_COMPACTO}
          qualidade={qualidade.nivel}
          ampla
        />

        <ProvedorQualidade nivel={qualidade.nivel}>
        <CenaTabletop
          objetos={objetos}
          idSelecionado={null}
          ferramenta="translate"
          aoSelecionar={NOOP}
          aoTransformar={NOOP}
          atendimento={atendimento}
          somenteLeitura
          exibirAsfalto={exibirAsfalto}
        />
        </ProvedorQualidade>

        <PosProcessamento qualidade={qualidade.nivel} oclusaoAmbiente />

        <OrbitControls makeDefault minDistance={2} maxDistance={30} maxPolarAngle={Math.PI / 2 - 0.02} enableDamping dampingFactor={0.08} />
      </Canvas>
    </div>
  )
}
